import React, { use, useEffect, useState } from "react";
import { ButtonType, buttonVariants } from "../reusable/Button";
import { cn } from "@/lib/utils";
import axios from "axios";
import SimpleReCaptcha from "@/components/ReCaptcha/SimpleReCaptcha";

interface ContactFormProps {
  data: {
    title: string;
    text: string;
    checkboxText: string;
    button: any[];
  };
}

const getSourcePage = () =>
  typeof window !== "undefined"
    ? `${window.location.pathname}${window.location.search}`
    : "";

export default function ContactForm({
  data: { title, text, checkboxText, button },
}: ContactFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [agreed, setAgreed] = useState(false);
  const [mounted, setMounted] = useState(false);

  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [recaptchaError, setRecaptchaError] = useState("");

  useEffect(() => {
    setMounted(true);
  }, []);

  const validateEmail = (email: string) => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return false;
    const parts = email.split("@");
    if (parts.length !== 2) return false;
    const domain = parts[1];
    if (!domain.includes(".")) return false;
    const tld = domain.split(".").pop();
    if (!tld || tld.length < 2) return false;
    return true;
  };

  const validatePhone = (phone: string) =>
    /^\+?\d{9,15}$/.test(phone.replace(/\s+/g, ""));

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");
    setRecaptchaError("");

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const phone = formData.get("phone") as string;

    let valid = true;

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address.");
      valid = false;
    } else {
      setEmailError("");
    }

    if (!validatePhone(phone)) {
      setPhoneError("Please enter a valid phone number.");
      valid = false;
    } else {
      setPhoneError("");
    }

    if (!valid) {
      setIsSubmitting(false);
      return;
    }

    if (!recaptchaToken) {
      setRecaptchaError("Please complete the reCAPTCHA verification.");
      setIsSubmitting(false);
      return;
    }

    const data = {
      firstName: formData.get("firstName") as string,
      lastName: formData.get("lastName") as string,
      phone,
      email,
      street: formData.get("street") as string,
      city: formData.get("city") as string,
      state: formData.get("state") as string,
      zip: formData.get("zip") as string,
      message: formData.get("message") as string,
      agreed,
      recaptchaToken,
      formName: "Contact Form",
      sourcePage: getSourcePage(),
    };

    try {
      const response = await axios.post("/api/send-interest", data);
      if (response.data.success) {
        setSubmitStatus("success");
        (e.target as HTMLFormElement).reset();
        setAgreed(false);
        setRecaptchaToken("");
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRecaptchaVerify = (token: string) => {
    setRecaptchaToken(token);
    setRecaptchaError("");
  };

  const handleRecaptchaError = (message: string) => {
    setRecaptchaToken("");
    setRecaptchaError(message);
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaToken("");
    setRecaptchaError("reCAPTCHA expired. Please complete it again.");
  };

  return (
    <section className="w-full bg-[#22344b] py-16 px-4 md:px-16 flex flex-col items-center">
      <div className="max-w-5xl w-full">
        <h2 className="font-serif text-secondary-light text-h2-mobile md:text-h2-tablet desktop:text-h2-desktop mb-2">
          {title}
        </h2>
        {mounted && (
          <p
            className="text-gray-white mb-8"
            dangerouslySetInnerHTML={{ __html: text }}
          ></p>
        )}
        {submitStatus === "success" && (
          <div className="mb-6 p-4 text-accent-color-three-dark border border-white rounded bg-secondary-light">
            Thank you! Your message has been sent successfully.
          </div>
        )}
        {submitStatus === "error" && (
          <div className="mb-6 p-4 text-red border border-red rounded">
            Sorry, there was an error sending your message. Please try again.
          </div>
        )}
        <form className="w-full" onSubmit={handleSubmit} autoComplete="off">
          <div className="flex flex-col md:flex-row gap-6 mb-4">
            <div className="flex-1 flex flex-col gap-2">
              <label className="text-base text-gray-white">First Name*</label>
              <input
                type="text"
                name="firstName"
                placeholder="Enter your first name."
                className="w-full p-3 rounded bg-[#f7f7f7] text-[#22344b] focus:outline-none"
                required
              />
            </div>
            <div className="flex-1 flex flex-col gap-2">
              <label className="text-base text-gray-white">Last Name*</label>
              <input
                type="text"
                name="lastName"
                placeholder="Enter your last name."
                className="w-full p-3 rounded bg-[#f7f7f7] text-[#22344b] focus:outline-none"
                required
              />
            </div>
            <div className="flex-1 flex flex-col gap-2">
              <label className="text-base text-gray-white">Phone Number*</label>
              <input
                type="tel"
                name="phone"
                placeholder="Enter your phone number."
                className="w-full p-3 rounded bg-[#f7f7f7] text-[#22344b] focus:outline-none"
                required
              />
              {phoneError && (
                <span className="text-red text-sm mt-1 block">
                  {phoneError}
                </span>
              )}
            </div>
            <div className="flex-1 flex flex-col gap-2">
              <label className="text-base text-gray-white">
                Email Address*
              </label>
              <input
                type="email"
                name="email"
                placeholder="Enter your email address."
                className="w-full p-3 rounded bg-[#f7f7f7] text-[#22344b] focus:outline-none"
                required
              />
              {emailError && (
                <span className="text-red text-sm mt-1 block">
                  {emailError}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-6 mb-4">
            <div className="flex-1 flex flex-col gap-2">
              <label className="text-base text-gray-white">
                Street Address
              </label>
              <input
                type="text"
                name="street"
                placeholder="Enter your street address."
                className="w-full p-3 rounded bg-[#f7f7f7] text-[#22344b] focus:outline-none"
              />
            </div>
            <div className="flex-1 flex flex-col gap-2">
              <label className="text-base text-gray-white">City</label>
              <input
                type="text"
                name="city"
                placeholder="Enter your city."
                className="w-full p-3 rounded bg-[#f7f7f7] text-[#22344b] focus:outline-none"
              />
            </div>
            <div className="flex-1 flex flex-col gap-2">
              <label className="text-base text-gray-white">
                State / Province
              </label>
              <input
                type="text"
                name="state"
                placeholder="Enter your state or province."
                className="w-full p-3 rounded bg-[#f7f7f7] text-[#22344b] focus:outline-none"
              />
            </div>
            <div className="flex-1 flex flex-col gap-2">
              <label className="text-base text-gray-white">
                ZIP / Postal Code
              </label>
              <input
                type="text"
                name="zip"
                placeholder="Enter your ZIP / Postal Code."
                className="w-full p-3 rounded bg-[#f7f7f7] text-[#22344b] focus:outline-none"
              />
            </div>
          </div>
          <div className="mb-4">
            <label className="text-base text-gray-white">Your Message*</label>
            <textarea
              name="message"
              placeholder="Enter your message."
              className="w-full p-3 rounded bg-[#f7f7f7] text-[#22344b] focus:outline-none"
              rows={5}
              required
            />
          </div>
          <div className="flex items-center mb-8">
            <input
              type="checkbox"
              id="agreed"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mr-2"
              required
            />
            <label htmlFor="agreed" className="text-gray-white text-sm">
              {checkboxText}
            </label>
          </div>
          <div className="flex justify-end">
            <div className="w-full">
              <SimpleReCaptcha
                onVerify={handleRecaptchaVerify}
                onError={handleRecaptchaError}
                onExpired={handleRecaptchaExpired}
              />
              {recaptchaError && (
                <span className="text-red text-sm mt-2 block">
                  {recaptchaError}
                </span>
              )}
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <button
              type="submit"
              disabled={isSubmitting || !agreed}
              className={cn(
                buttonVariants({ type: button[0].item.type as ButtonType }),
                "leading-6 tracking-[0.15em] text-[18px]"
              )}
            >
              {isSubmitting ? button[0].item.title : "Submit Message"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
